// ─────────────────────────────────────────────────────────────────────────────
// Suivi des cibles de masse salariale (pulse_payroll_targets/{year}) pour la
// page « Masse salariale réelle ». Fonctions PURES, client-safe (aucun import
// Firestore) — on part des RealPayrollRow déjà calculées côté serveur.
// Contrairement à aggregateByTeam (graphiques de la page Grille salariale),
// l'agrégation se fait par teamId (clé stable des cibles), sur les lignes déjà
// filtrées par le prédicat de comptage de la page, et le coût INCLUT le boni
// (realPayrollRowCost) — cohérent avec la carte « Masse salariale réelle ».
// ─────────────────────────────────────────────────────────────────────────────

import {
  realPayrollRowCost,
  type RealPayrollRow,
} from './types'
import type { YearRevenueOutlook } from '../finance/computations'

// Cible par défaut du ratio masse salariale ÷ revenus quand aucune config
// n'existe pour l'année (fraction).
export const DEFAULT_REVENUE_TARGET = 0.15

const NO_TEAM_KEY = '__none__'
const NO_TEAM_LABEL = 'Sans équipe'

export type TeamTargetRow = {
  teamId: string | null // null = « Sans équipe »
  teamName: string
  cost: number // realPayrollRowCost (boni inclus)
  headcount: number
  sharePct: number // part de la masse totale (fraction)
  targetPct: number | null // cible (fraction) ; null = aucune cible
  deltaPct: number | null // sharePct − targetPct (points, en fraction)
}

// Agrège les lignes (déjà filtrées par le prédicat de comptage de la page —
// pas de re-filtre `active` ici) par teamId et croise avec les cibles.
// Les cibles orphelines (teamId sans ligne) ne produisent pas de ligne : elles
// restent visibles côté admin et sont purgées à la prochaine sauvegarde.
// Tri par coût décroissant, « Sans équipe » toujours en dernier.
export function buildTeamTargetRows(
  rows: RealPayrollRow[],
  targets: Record<string, number>,
): TeamTargetRow[] {
  const byTeam = new Map<
    string,
    { teamId: string | null; teamName: string; cost: number; headcount: number }
  >()
  for (const row of rows) {
    const key = row.teamId ?? NO_TEAM_KEY
    const acc =
      byTeam.get(key) ??
      {
        teamId: row.teamId ?? null,
        teamName: row.teamName ?? NO_TEAM_LABEL,
        cost: 0,
        headcount: 0,
      }
    acc.cost += realPayrollRowCost(row)
    acc.headcount += 1
    byTeam.set(key, acc)
  }

  const total = [...byTeam.values()].reduce((s, t) => s + t.cost, 0)

  return [...byTeam.values()]
    .map((t) => {
      const targetPct = t.teamId !== null ? (targets[t.teamId] ?? null) : null
      const sharePct = total > 0 ? t.cost / total : 0
      return {
        ...t,
        sharePct,
        targetPct,
        deltaPct: targetPct !== null ? sharePct - targetPct : null,
      }
    })
    .sort((a, b) => {
      // « Sans équipe » en dernier, peu importe son poids.
      if (a.teamId === null !== (b.teamId === null)) return a.teamId === null ? 1 : -1
      return b.cost - a.cost
    })
}

export type PayrollRatio = {
  ratioProjected: number | null // masse ÷ revenu projeté ; null si revenu 0
  ratioBudget: number | null // masse ÷ revenu budget annuel ; null si budget 0
  targetPct: number // cible (fraction)
  deltaPts: number | null // ratioProjected − cible (points, en fraction)
}

// Ratio masse salariale ÷ revenus de l'année. Le numérateur doit être la masse
// ANNUELLE pleine année (le dénominateur — projection des revenus — l'est).
export function computePayrollRatio(
  annualPayroll: number,
  outlook: YearRevenueOutlook,
  targetPct: number,
): PayrollRatio {
  const ratioProjected =
    outlook.revenueProjected > 0 ? annualPayroll / outlook.revenueProjected : null
  const ratioBudget =
    outlook.revenueBudgetYear > 0 ? annualPayroll / outlook.revenueBudgetYear : null
  return {
    ratioProjected,
    ratioBudget,
    targetPct,
    deltaPts: ratioProjected !== null ? ratioProjected - targetPct : null,
  }
}

export type BudgetRevenueCheck = {
  ratio: number | null // masse ÷ revenu budget annuel ; null si budget 0
  targetPct: number // cible (fraction)
  deltaPts: number | null // ratio − cible (points, en fraction)
  // Revenu annuel total requis pour que la masse respecte la cible = masse ÷ cible.
  revenueNeeded: number
  // Revenus SUPPLÉMENTAIRES à aller chercher vs le budget actuel pour atteindre
  // la cible ; 0 quand le budget suffit déjà.
  revenueShortfall: number
}

// Vérifie si la masse prévue respecte la cible « masse = X % des revenus » face
// au revenu BUDGÉTÉ de l'année (vision plan vs plan — carte de l'onglet
// Graphiques). En cas de dépassement, chiffre le manque à gagner en revenus.
export function checkBudgetRevenueTarget(
  payroll: number,
  revenueBudgetYear: number,
  targetPct: number,
): BudgetRevenueCheck {
  const ratio = revenueBudgetYear > 0 ? payroll / revenueBudgetYear : null
  const revenueNeeded = targetPct > 0 ? payroll / targetPct : 0
  return {
    ratio,
    targetPct,
    deltaPts: ratio !== null ? ratio - targetPct : null,
    revenueNeeded,
    revenueShortfall:
      targetPct > 0 ? Math.max(0, revenueNeeded - revenueBudgetYear) : 0,
  }
}
